import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TProject } from '../../../../models/projects/project.model';
import { TApiResponse } from '../../api.interfaces';
import { allowStatus400 } from '../../decorators';
import { TSettingItemTable } from '../../../../pages/projects/settings/versions/setting-item.component';

@Injectable()
export class SettingsApi {
  constructor(private readonly httpClient: HttpClient) {}

  async getSettingsPageContent(projectAlias: string) {
    const result = await this.httpClient
      .get(`projects/project/${projectAlias}/settings/page-content`)
      .toPromise();
    return result as { data: { project: TProject } };
  }

  @allowStatus400()
  async createSetting(projectAlias: string, formData: { name: string }) {
    const result = (await this.httpClient
      .post(`projects/project/${projectAlias}/settings/create`, formData)
      .toPromise()) as TApiResponse<void>;
    return result;
  }

  @allowStatus400()
  async createSettingVersion(projectAlias: string, formData: { name: string }) {
    const result = (await this.httpClient
      .post(
        `projects/project/${projectAlias}/settings/create-setting-version`,
        formData,
      )
      .toPromise()) as TApiResponse<void>;
    return result;
  }

  @allowStatus400()
  async activateSettingsModule(projectAlias: string, repositoryName: string) {
    const result = (await this.httpClient
      .post(`projects/project/${projectAlias}/settings/activate`, {
        repositoryName,
      })
      .toPromise()) as TApiResponse<TProject>;
    return result;
  }

  async getSettingContent(
    projectAlias: string,
    settingName: string,
    version: string,
  ) {
    const result = (await this.httpClient
      .get(
        `projects/project/${projectAlias}/settings/setting/${settingName}/get/${version}`,
      )
      .toPromise()) as TApiResponse<any>;
    return result;
  }

  async setSettingContent(
    projectAlias: string,
    settingName: string,
    version: string,
    data: any,
  ) {
    const result = (await this.httpClient
      .post(
        `projects/project/${projectAlias}/settings/setting/${settingName}/set/${version}`,
        {
          data,
        },
      )
      .toPromise()) as TApiResponse<any>;
    return result;
  }

  async exportToCDN(projectAlias: string, version: string) {
    const result = (await this.httpClient
      .post(
        `projects/project/${projectAlias}/settings/version/${version}/export`,
        {},
      )
      .toPromise()) as TApiResponse<any>;
    return result;
  }

  async resetSettingToDefault(
    projectAlias: string,
    settingName: string,
    version: string,
  ) {
    const result = (await this.httpClient
      .post(
        `projects/project/${projectAlias}/settings/setting/${settingName}/reset-to-default/${version}`,
        {},
      )
      .toPromise()) as TApiResponse<any>;
    return result;
  }

  async renameSetting(
    projectAlias: string,
    settingName: string,
    newName: string,
  ) {
    const result = (await this.httpClient
      .post(
        `projects/project/${projectAlias}/settings/setting/${settingName}/rename`,
        { newName },
      )
      .toPromise()) as TApiResponse<{ project: TProject }>;
    return result;
  }

  @allowStatus400()
  async editSetting(
    projectAlias: string,
    settingName: string,
    field: string,
    value: any,
  ) {
    const result = (await this.httpClient
      .post(
        `projects/project/${projectAlias}/settings/setting/${settingName}/edit`,
        { field, value },
      )
      .toPromise()) as TApiResponse<{ project: TProject }>;
    return result;
  }

  async deleteSetting(projectAlias: string, settingName: string) {
    const result = (await this.httpClient
      .post(
        `projects/project/${projectAlias}/settings/setting/${settingName}/delete`,
        {},
      )
      .toPromise()) as TApiResponse<{ project: TProject }>;
    return result;
  }

  async renameVersion(
    projectAlias: string,
    versionName: string,
    newName: string,
  ) {
    const result = (await this.httpClient
      .post(
        `projects/project/${projectAlias}/settings/version/${versionName}/rename`,
        { newName },
      )
      .toPromise()) as TApiResponse<{ project: TProject }>;
    return result;
  }

  @allowStatus400()
  async editVersion(
    projectAlias: string,
    versionName: string,
    field: string,
    value: any,
  ) {
    const result = (await this.httpClient
      .post(
        `projects/project/${projectAlias}/settings/version/${versionName}/edit`,
        { field, value },
      )
      .toPromise()) as TApiResponse<{ project: TProject }>;
    return result;
  }

  async deleteVersion(projectAlias: string, versionName: string) {
    const result = (await this.httpClient
      .post(
        `projects/project/${projectAlias}/settings/version/${versionName}/delete`,
        {},
      )
      .toPromise()) as TApiResponse<{ project: TProject }>;
    return result;
  }

  async getVersionSettingsTableData(
    projectAlias: string,
    versionName: string,
    compareVersion: string,
  ) {
    const result = (await this.httpClient
      .post(
        `projects/project/${projectAlias}/settings/version/${versionName}/table-settings-list`,
        { compareVersion },
      )
      .toPromise()) as TApiResponse<{
      result: Omit<TSettingItemTable, 'isSelected'>[];
    }>;
    return result;
  }

  async pullSettingsListContent(
    projectAlias: string,
    toVersion: string,
    fromVersion: string,
    settingsList: string[],
    trackDefault: boolean,
  ) {
    const result = (await this.httpClient
      .post(
        `projects/project/${projectAlias}/settings/version/${toVersion}/pull-settings-list-content`,
        { fromVersion, settingsList, trackDefault },
      )
      .toPromise()) as TApiResponse<{}>;
    return result;
  }
}
